import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';

type Country = { iso: string; name: string; dial: string };

/** Uganda first (the default), then neighbours and the places buyers and importers call from. */
const PHONE_COUNTRIES: readonly Country[] = [
  { iso: 'UG', name: 'Uganda', dial: '256' },
  { iso: 'KE', name: 'Kenya', dial: '254' },
  { iso: 'TZ', name: 'Tanzania', dial: '255' },
  { iso: 'RW', name: 'Rwanda', dial: '250' },
  { iso: 'BI', name: 'Burundi', dial: '257' },
  { iso: 'SS', name: 'South Sudan', dial: '211' },
  { iso: 'CD', name: 'DR Congo', dial: '243' },
  { iso: 'ET', name: 'Ethiopia', dial: '251' },
  { iso: 'SO', name: 'Somalia', dial: '252' },
  { iso: 'ZM', name: 'Zambia', dial: '260' },
  { iso: 'ZA', name: 'South Africa', dial: '27' },
  { iso: 'NG', name: 'Nigeria', dial: '234' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '971' },
  { iso: 'GB', name: 'United Kingdom', dial: '44' },
  { iso: 'US', name: 'United States', dial: '1' },
  { iso: 'IN', name: 'India', dial: '91' },
  { iso: 'CN', name: 'China', dial: '86' },
  { iso: 'JP', name: 'Japan', dial: '81' },
];

const flag = (iso: string) =>
  String.fromCodePoint(...[...iso].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));

type Props<T extends FieldValues, TOut = T> = {
  control: Control<T, unknown, TOut>;
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  defaultCountry?: string;
};

/**
 * A country picker beside the number. The form value is the whole international
 * number ("+256700467889"), so what is saved needs no guessing later. A leading
 * 0 is dropped ("0700…" becomes "+256700…"), and the value is empty until a
 * number is typed so a required check still works.
 */
export function PhoneInputField<T extends FieldValues, TOut = T>({
  control,
  name,
  label,
  required = false,
  placeholder = '700 000 000',
  helperText,
  defaultCountry = 'UG',
}: Props<T, TOut>) {
  const [iso, setIso] = useState(defaultCountry);
  const country = PHONE_COUNTRIES.find((c) => c.iso === iso) ?? PHONE_COUNTRIES[0]!;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value = String(field.value ?? '');
        const national = value.startsWith(`+${country.dial}`) ? value.slice(country.dial.length + 1) : '';
        const compose = (c: Country, digits: string) => {
          const clean = digits.replace(/\D/g, '').replace(/^0+/, '').slice(0, 15 - c.dial.length);
          return clean ? `+${c.dial}${clean}` : '';
        };

        return (
          <TextField
            label={label}
            value={national}
            onChange={(e) => field.onChange(compose(country, e.target.value))}
            onBlur={field.onBlur}
            inputRef={field.ref}
            name={field.name}
            type="tel"
            required={required}
            placeholder={placeholder}
            autoComplete="tel-national"
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ?? helperText}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { inputMode: 'tel' },
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 1 }}>
                    <Select
                      variant="standard"
                      disableUnderline
                      value={iso}
                      onChange={(e) => {
                        const next = PHONE_COUNTRIES.find((c) => c.iso === e.target.value) ?? country;
                        setIso(next.iso);
                        field.onChange(compose(next, national));
                      }}
                      inputProps={{ 'aria-label': 'Country code' }}
                      renderValue={() => (
                        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                          {flag(country.iso)} +{country.dial}
                        </Box>
                      )}
                      MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
                    >
                      {PHONE_COUNTRIES.map((c) => (
                        <MenuItem key={c.iso} value={c.iso}>
                          {flag(c.iso)}&nbsp;{c.name}&nbsp;(+{c.dial})
                        </MenuItem>
                      ))}
                    </Select>
                  </InputAdornment>
                ),
              },
            }}
          />
        );
      }}
    />
  );
}
